import React, { Fragment, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import { Spotlight } from '@atlaskit/onboarding';

import { SPOTLIGHT_ONBOARDING_STEP1_TARGET } from '../../../common/constants/spotlight';
import { type FireAnalyticsEvent } from '../../../common/types';
import { useAnalytics } from '../../../controllers/analytics';
import { useAvailableProducts } from '../../../controllers/store/bootstrap';
import { useSpotlightState } from '../../../controllers/store/spotlight';

import messages from './messages';

const spotlightAnalyticsPayload = (action: string, attributes: FireAnalyticsEvent['attributes']) =>
	({
		actionSubject: 'rovoChangeboardingSpotlight',
		attributes: {
			additionalAttributes: attributes,
		},
		eventType: 'ui',
		action,
	}) satisfies FireAnalyticsEvent;

export const SearchPageOnboardingSpotlight = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [spotlight, { dismiss }] = useSpotlightState();
	const { fireAnalyticsEvent } = useAnalytics();
	const availableProducts = useAvailableProducts();

	const renderSpotlight = spotlight.state === 'visible' && availableProducts.length > 0; // Only render the spotlight after available products have been fetched

	useEffect(() => {
		if (renderSpotlight) {
			fireAnalyticsEvent(
				spotlightAnalyticsPayload('view spotlight', {
					step: spotlight.activeSpotlight + 1, // activeSpotlight starts at 0
				}),
			);
		}
	}, [fireAnalyticsEvent, renderSpotlight, spotlight]);

	if (!renderSpotlight) {
		return null;
	}

	const renderActiveSpotlight = () => {
		const spotlights = [
			<Spotlight
				actions={[
					{
						onClick: () => {
							dismiss();

							fireAnalyticsEvent(
								spotlightAnalyticsPayload('click ok', {
									step: spotlight.activeSpotlight + 1, // activeSpotlight starts at 0
								}),
							);
						},
						text: formatMessage(messages.ok),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.onboardingStep1Heading)}
				target={SPOTLIGHT_ONBOARDING_STEP1_TARGET}
				key={SPOTLIGHT_ONBOARDING_STEP1_TARGET}
				dialogWidth={275}
				dialogPlacement="left top"
				targetRadius={8}
			>
				{formatMessage(messages.onboardingStep1Description)}
			</Spotlight>,
		];

		return spotlights[spotlight.activeSpotlight];
	};

	return <Fragment>{renderActiveSpotlight()}</Fragment>;
};
